import { z } from "zod";
import {
  decimalPlacesDataSource,
  digitGroupingDataSource,
  largeNumberAbbreviationDataSource,
} from "../../helpers/estimateConsts.js";
import {
  objectIdString,
  filterWhereClause,
  filterWhereClauseFor,
  dynamicCustomFieldPatterns,
  sortConfig,
  sortConfigFor,
  statusResponse,
  extendStatusResponse,
} from "../../helpers/schemas/common.schema.js";
import { zPlainTextRequired, zSafeHtml } from "../middleware/zod-sanitize.js";
import { decimalPlacesObj, estimateSettingsObj } from "./estimates.collection.js";
import type { ClientSession } from "mongodb";

// ============================================================================
// Field-restricted filter `where` clauses (replace the OPEN filterWhereClause)
// Each list is exactly the columns the corresponding grid exposes for filtering;
// dynamicCustomFieldPatterns additionally permits per-project custom-field /
// form-question columns. See filterWhereClauseFor in common.schema.ts.
// ============================================================================

// /getEstimateList grid (estimate list columns).
const ESTIMATE_LIST_FILTERABLE_FIELDS = [
  "counter",
  "name",
  "description",
  "costIndex",
  "amount",
  "projectId",
  "projectName",
  "spaceName",
  "publishedVersionNumber",
  "costDatabase.name",
  "createdBy.fullName",
  "createdBy",
  "createdDate",
  "statusInReview.name",
] as const;
const estimateListWhere = filterWhereClauseFor(ESTIMATE_LIST_FILTERABLE_FIELDS, {
  patterns: dynamicCustomFieldPatterns,
});

// /getEstimateFiles grid (BOQ files tab columns).
const ESTIMATE_FILES_FILTERABLE_FIELDS = ["fileName", "addedDate", "addedBy"] as const;
const estimateFilesWhere = filterWhereClauseFor(ESTIMATE_FILES_FILTERABLE_FIELDS, {
  patterns: dynamicCustomFieldPatterns,
});

// /getEstimateItems grid (BOQ items columns).
const ESTIMATE_ITEMS_FILTERABLE_FIELDS = [
  "code",
  "description",
  "rate",
  "params.purchaseUnit.name",
  "amount",
  "quantity",
  "itemId",
  "parentId",
] as const;
const estimateItemsWhere = filterWhereClauseFor(ESTIMATE_ITEMS_FILTERABLE_FIELDS, {
  patterns: dynamicCustomFieldPatterns,
});

// /chargesList grid (portal charges columns).
const CHARGES_LIST_FILTERABLE_FIELDS = ["name", "percentage", "modifiedBy._id", "modifiedDate"] as const;
const chargesListWhere = filterWhereClauseFor(CHARGES_LIST_FILTERABLE_FIELDS, { patterns: dynamicCustomFieldPatterns });
// SORTABLE set DIVERGES from the filterable set: the charges grid's "Modified by" column has
// col.field = "modifiedBy.name" (SlickGrid sends the sort name = col.field), whereas the FILTER
// column is keyed by "modifiedBy._id". getChargesList's sort mapper is empty, so the sort name is
// dropped straight into the Mongo $sort — allow the exact sortable column names the grid emits.
export const CHARGES_LIST_SORTABLE_FIELDS = ["name", "percentage", "modifiedBy.name", "modifiedDate"] as const;

// ============================================================================
// Core estimate schemas (moved from helpers/schemas/estimate.schema.ts)
// ============================================================================

/**
 * Flat WRITE payload for saving estimate settings (transformed to MongoDB dot-notation
 * in the `saveEstimateSettings` service). Intentionally diverges from the canonical
 * stored shape — top-level decimal keys instead of nested `decimalPlaces`, `.enum(...)`
 * validators instead of free-form strings, and `{level, value}` numberingSystem instead
 * of `number[]`. For the canonical stored shape see {@link estimateSettingsObj} in
 * `estimates.collection.ts`.
 */
export const saveEstimateSettingsSchema = z.object({
  estimateId: z
    .string()
    .trim()
    .length(24)
    .regex(/^[a-fA-F0-9]{24}$/)
    .meta({
      description: "Estimate unique id",
      example: "64b2c123df890abc1234abcd",
    }),
  quantity: z
    .number()
    .refine((val) => decimalPlacesDataSource.map((field: { value: number }) => field.value).includes(val))
    .optional()
    .meta({
      description: "Quantity rounding preference",
      example: 1,
    }),
  measurement: z
    .number()
    .refine((val) => decimalPlacesDataSource.map((field: { value: number }) => field.value).includes(val))
    .optional()
    .meta({
      description: "Measurement rounding preference",
      example: 1,
    }),
  takeoff: z
    .number()
    .refine((val) => decimalPlacesDataSource.map((field: { value: number }) => field.value).includes(val))
    .optional()
    .meta({
      description: "Takeoff rounding preference",
      example: 1,
    }),
  rate: z
    .number()
    .refine((val) => decimalPlacesDataSource.map((field: { value: number }) => field.value).includes(val))
    .optional()
    .meta({
      description: "Rate rounding preference",
      example: 1,
    }),
  amount: z
    .number()
    .refine((val) => decimalPlacesDataSource.map((field: { value: number }) => field.value).includes(val))
    .optional()
    .meta({
      description: "Amount rounding preference",
      example: 1,
    }),
  digitGrouping: z
    .enum(digitGroupingDataSource.map((field: { value: string }) => field.value))
    .optional()
    .meta({
      description:
        "Digit-grouping style for displayed numbers (one of the allowed digitGroupingDataSource values, e.g. thousands/lakh grouping). Written to settings.digitGrouping only when present.",
    }),
  largeNumber: z
    .enum(largeNumberAbbreviationDataSource.map((field: { value: string }) => field.value))
    .optional()
    .meta({
      description:
        "Large-number abbreviation style (one of the allowed largeNumberAbbreviationDataSource values, e.g. K/M or Lakh/Crore). Written to settings.largeNumber only when present.",
    }),
  numberingSystem: z
    .object({
      // level indexes into a dot-path key (settings.numberingSystem.<level>); a fractional or
      // negative value corrupts the settings object, so constrain to a non-negative integer.
      // Cap the level: it indexes a fixed-length numberingSystem array (defaultEstimateSettings uses
      // Array(10)); an out-of-range level (e.g. 500000) pads the settings array with hundreds of
      // thousands of nulls on both EstimateVersions and the Estimates embed → document-bloat DoS.
      level: z.number().int().min(0).max(9).optional(),
      // value selects a numbering style; keep it a non-negative integer so getSymbolForNumber never
      // renders garbage/blank for a fractional or negative index.
      value: z.number().int().nonnegative().optional(),
    })
    .optional()
    .meta({
      description: "Payload to save estimate general preference",
    }),
});

// Must validate hex format, not just length: a 24-char NON-hex string passes .length(24)
// but throws `BSONError: input must be a 24 character hex string` when the service does
// `new ObjectId(value)`. The regex makes the schema guarantee what the service assumes.
const objectIdStringSchema = z
  .string()
  .trim()
  .length(24)
  .regex(/^[a-fA-F0-9]{24}$/, "Invalid ObjectId format");

// A BOQ parentId is either the "0" root sentinel or a 24-char hex item ObjectId. The service does
// `if (parentId !== "0") new ObjectId(parentId)`, so a non-hex/wrong-length string (which plain
// z.string() allowed) throws a BSONError. Restrict to those two shapes at the boundary.
const parentIdStringSchema = z
  .string()
  .refine((v) => v === "0" || /^[a-fA-F0-9]{24}$/.test(v), "parentId must be '0' or a valid ObjectId");

export const estimateListParamsSchema = z.object({
  estimateId: objectIdStringSchema
    .optional()
    .meta({ description: "Scope the list to a single estimate by its _id (ObjectId 24-hex, from getEstimateList)." }),
  spaceId: objectIdStringSchema
    .optional()
    .meta({ description: "Scope the list to a space (ObjectId 24-hex, from projects_getSpacesDataByProjectId)." }),
  projectId: objectIdStringSchema.optional().nullable().meta({
    description:
      "Scope the list to a project (ObjectId 24-hex, from getIncludedProjectsController); null/omitted = not scoped by project.",
  }),
  versionId: objectIdStringSchema.optional().meta({
    description: "Optional EstimateVersions _id to read a specific version's list row instead of the latest.",
  }),
  getEstimatesWithNamedVersion: z
    .boolean()
    .optional()
    .meta({ description: "If true, fold in published named-version rows in addition to the latest draft." }),
});

export const estimateListQuerySchema = z
  .object({
    key: z.unknown().optional().meta({ description: "Legacy dispatcher key; ignored by the list-only endpoint." }),
    where: estimateListWhere.meta({
      description:
        "Grid filter clause restricted to the estimate-list filterable columns (plus per-project custom-field columns).",
    }),
    skip: z.number().int().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
    take: z.number().int().positive().optional().meta({ description: "Page size (max rows to return)." }),
    requiresCounts: z.boolean().optional().meta({ description: "If true, also return the total matching count." }),
    // Sort restricted to the estimate-list columns (== the grid's sortable col.field set, which is
    // a subset of ESTIMATE_LIST_FILTERABLE_FIELDS; the getEstimateList/getEstimateListWithNamedVersions
    // sort mapper keys name/description/costIndex/spaceName/createdBy are all within it) plus per-project
    // custom-field columns. Rejects any other sort column at the Zod boundary (400).
    sorted: sortConfigFor(ESTIMATE_LIST_FILTERABLE_FIELDS, { patterns: dynamicCustomFieldPatterns }).meta({
      description: "Ordered list of column/direction sort specs, restricted to the estimate-list sortable columns.",
    }),
    nextPageToken: z
      .string()
      .nullable()
      .optional()
      .meta({ description: "Opaque cursor for the next page, echoed from a prior response." }),
    params: estimateListParamsSchema
      .optional()
      .meta({ description: "Scoping filters (space/project/estimate/version) applied to the list." }),
    getEstimatesWithNamedVersion: z
      .boolean()
      .optional()
      .meta({ description: "If true, fold in published named-version rows alongside the latest draft." }),
    spaceId: objectIdStringSchema
      .optional()
      .meta({ description: "Root-level space scope (ObjectId 24-hex); alternative to params.spaceId." }),
    projectId: objectIdStringSchema
      .optional()
      .meta({ description: "Root-level project scope (ObjectId 24-hex); alternative to params.projectId." }),
    portalId: objectIdStringSchema.optional().meta({
      description: "Injected server-side from the auth context; callers omit (any value sent is overridden).",
    }),
  })
  .strict();

const estimateListInsertValueSchema = z
  .object({
    // Non-empty: the service stores this straight into the version doc as the estimate name.
    name: z
      .string()
      .trim()
      .min(1)
      .meta({ description: "Estimate display name; stored on the new estimate's first version. Required, non-empty." }),
    description: z.string().optional().meta({ description: "Optional free-text description for the new estimate." }),
    dsrId: objectIdStringSchema.nullable().optional().meta({
      description:
        "Cost-database (DSR) ObjectId 24-hex to link on creation (from the cost-database/DSR list); null/omitted creates an estimate with no cost DB (a project default currency is used). Must resolve to a published cost DB or creation fails.",
    }),
    costIndex: z.number().optional().meta({
      description: "Price-adjustment factor for the estimate; must be > 0 (a value <= 0 is rejected). Omit to default.",
    }),
    spaceId: objectIdStringSchema.meta({
      description:
        "Space ObjectId 24-hex the estimate is created in (from projects_getSpacesDataByProjectId); create permission on this space for the ESTIMATES module is required.",
    }),
    projectId: objectIdStringSchema.meta({
      description:
        "Project ObjectId 24-hex the estimate belongs to (from getIncludedProjectsController); must resolve to an existing project.",
    }),
    inheritPermission: z.boolean().optional().meta({
      description:
        "If true, the new estimate inherits the space's permission/role assignments in the file system (creator becomes RESOURCE_ADMIN). If false or OMITTED, the estimate is created accessible to the creator only (the service normalizes an omitted value to false and usersRoles to {}).",
    }),
    // Values are numeric role ids; z.unknown() let a caller inject Mongo operators (e.g. {$ne:null}).
    usersRoles: z.record(z.string(), z.number()).optional().meta({
      description:
        "Map of userId -> numeric role id to seed explicit per-user permissions on the estimate in the file system (used when not inheriting).",
    }),
    // Only "merge" is branched on (anything else == standard); merge-specific validation
    // (non-empty constituentEstimateIds) is enforced in the createEstimate service.
    estimateType: z.string().optional().meta({
      description:
        'Estimate kind; only "merge" is special-cased (aggregates constituents), any other value (default) creates a standard estimate. For merge estimates prefer the createMergeEstimate endpoint.',
    }),
    constituentEstimateIds: z.array(objectIdStringSchema).optional().meta({
      description:
        'Only used when estimateType is "merge": the estimate _ids (each ObjectId 24-hex, from getEstimateList) to merge; ignored for standard estimates.',
    }),
  })
  .strict();

const estimateListUpdateValueSchema = z
  .object({
    _id: objectIdStringSchema.meta({
      description:
        "The estimate's _id to update (ObjectId 24-hex, from getEstimateList) — the estimate id, NOT its name.",
    }),
    costIndex: z.number().optional().meta({
      description:
        "New price-adjustment factor; applied only when > 0 (values <= 0 are ignored). Recalculates item rates accordingly.",
    }),
    // Non-empty when present: the service ignores an empty/whitespace name (silent no-op),
    // so reject it up front rather than silently dropping the rename.
    name: z
      .string()
      .trim()
      .min(1)
      .optional()
      .meta({ description: "New estimate name; an empty/whitespace value is a silent no-op (rename skipped)." }),
    description: z
      .string()
      .nullable()
      .optional()
      .meta({ description: "New description; pass null to clear it, omit to leave unchanged." }),
    upgradeDsrVersion: z.boolean().optional().meta({
      description:
        "If true, upgrade the linked cost database (DSR) to its latest published version and re-price DSR items.",
    }),
    forceDeletedItemToManual: z.boolean().optional().meta({
      description:
        "Only meaningful with upgradeDsrVersion: convert items no longer present in the newer DSR version into manual items instead of failing.",
    }),
  })
  .strict();

// List-only request. The frontend grid sends `{ value: <query>, params? }`; the legacy bare-query
// form (query fields at the root) is also accepted. Estimate create/update/delete now have their
// own dedicated endpoints (createEstimateRequest / updateEstimateDataRequest / deleteEstimateRequest).
export const getEstimateListRequestSchema = z.union([
  estimateListQuerySchema,
  z
    .object({
      value: estimateListQuerySchema,
      params: estimateListParamsSchema.optional(),
    })
    .strict(),
]);

/**
 * Constituent estimate version detail for merge estimates.
 * Declared here (before estimateListItemSchema) so it can be referenced
 * both in the list schema and in the estimateDataSchema below.
 */
const constituentEstimateVersionDetailSchema = z
  .object({
    versionId: z.string().meta({ description: "Version document ID" }),
    counter: z.number().meta({ description: "Estimate counter number" }),
    name: z.string().optional().meta({ description: "Estimate name" }),
    versionNumber: z.number().optional().meta({ description: "Version number" }),
  })
  .meta({ description: "Detail of a constituent estimate version in a merge estimate" });

const estimateListStatusInReviewSchema = z.object({
  _id: z.string().optional(),
  name: z.string().optional(),
  category: z.string().optional(),
});

const estimateListCreatedBySchema = z.object({
  _id: objectIdStringSchema,
  fullName: z.string().optional(),
});

const estimateListCostDatabaseSchema = z.object({
  name: z.string().optional(),
});

export const estimateListItemSchema = z
  .object({
    id: objectIdStringSchema.optional(),
    _id: objectIdStringSchema.optional(),
    projectId: z.string().optional(),
    projectName: z.string().optional(),
    spaceId: objectIdStringSchema.optional(),
    spaceName: z.string().optional(),
    statusInReview: estimateListStatusInReviewSchema.optional(),
    counter: z.string(),
    createdBy: estimateListCreatedBySchema.optional(),
    createdDate: z.number().optional(),
    versionId: objectIdStringSchema.optional(),
    name: z.string().optional(),
    description: z.string().nullable().optional(),
    amount: z.number().optional(),
    additionalCost: z
      .array(
        z.object({
          value: z.number().optional(),
        }),
      )
      .optional(),
    costIndex: z.union([z.string(), z.number()]).nullable().optional(),
    costDatabase: estimateListCostDatabaseSchema.optional(),
    versionNumber: z.number().nullable().optional(),
    settings: estimateSettingsObj.partial().extend({ decimalPlaces: decimalPlacesObj.partial().optional() }).optional(),
    publishedVersionNumber: z.number().nullable().optional(),
    estimateType: z.string().optional(),
    constituentEstimateVersionDetails: z.array(constituentEstimateVersionDetailSchema).optional(),
    userPermissions: z.array(z.number()),
  })
  .strip();

export const getEstimateListResponseSchema = z
  .object({
    status: z.boolean(),
    result: z.array(estimateListItemSchema).optional(),
    count: z.number().nonnegative().optional(),
    errorMessage: z.string().optional(),
    errorCode: z.number().optional(),
  })
  .strip();

export const getEstimateDataRequestSchema = z
  .object({
    _id: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) — the id, not the name.",
    }),
    versionId: objectIdStringSchema.nullable().optional().meta({
      description:
        "Optional EstimateVersions _id to read a specific version's data; null/omitted returns the latest version. Resolved together with _id.",
    }),
  })
  .strict();

/**
 * Currency shape embedded in estimate cost database.
 * Used by the frontend to format monetary values with the correct symbol.
 */
export const estimateCurrencySchema = z
  .object({
    id: z.string().meta({ description: "Currency identifier" }),
    category: z
      .enum(["Prefix", "Suffix"])
      .optional()
      .meta({ description: "Whether the symbol appears before or after the amount" }),
    selectedSymbol: z
      .object({
        name: z.string().meta({ description: "Display symbol e.g. '$', '₹'" }),
      })
      .meta({ description: "The selected currency symbol" }),
  })
  .meta({ description: "Currency configuration for an estimate" });

/**
 * Shape of the estimate data returned by the getEstimateData API.
 * This is the merged document from the Estimates collection spread with latestEstimateVersion fields.
 */
// Serialized form of estimates.collection displayUnitObj (ObjectIds arrive as strings over JSON).
const displayUnitSerializedSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().optional(),
    factor: z.number().optional(),
    selectedSymbol: z.object({ id: z.string().optional(), name: z.string().optional() }).optional(),
    type: z.object({ id: z.string().optional(), name: z.string().optional() }).optional(),
  })
  .passthrough();

export const estimateDataSchema = z
  .object({
    _id: z.string().optional().meta({ description: "Estimate document ID" }),
    userRole: z.number().optional().meta({ description: "User role level on this estimate" }),
    linearDisplayUnit: displayUnitSerializedSchema.optional(),
    areaDisplayUnit: displayUnitSerializedSchema.optional(),
    volumeDisplayUnit: displayUnitSerializedSchema.optional(),
    countDisplayUnit: displayUnitSerializedSchema.optional(),
    angleDisplayUnit: displayUnitSerializedSchema.optional(),
    name: z.string().optional().meta({ description: "Estimate name" }),
    counter: z
      .union([z.string(), z.number()])
      .optional()
      .meta({ description: "Formatted estimate counter e.g. 'ES-0001'" }),
    costDatabase: z
      .object({
        currency: estimateCurrencySchema,
      })
      .optional()
      .meta({ description: "Cost database linked to this estimate with its currency config" }),
    settings: estimateSettingsObj
      .partial()
      .extend({ decimalPlaces: decimalPlacesObj.partial().optional() })
      .optional()
      .meta({
        description:
          "Estimate display settings (response shape: all fields optional; canonical SSOT is estimateSettingsObj in estimates.collection.ts)",
      }),
    estimateType: z.string().optional().meta({ description: "Type of estimate e.g. 'merge'" }),
    constituentEstimateVersionDetails: z
      .array(constituentEstimateVersionDetailSchema)
      .optional()
      .meta({ description: "Constituent estimate versions for merge estimates" }),
    project: z
      .object({ _id: z.string().optional() })
      .optional()
      .meta({ description: "Project reference embedded in the estimate document" }),
    space: z
      .object({ _id: z.string().optional() })
      .optional()
      .meta({ description: "Space reference embedded in the estimate document" }),
    costDatabaseArray: z
      .array(
        z
          .object({
            currency: estimateCurrencySchema.optional(),
          })
          .passthrough(),
      )
      .optional()
      .meta({ description: "Array of cost databases for merge estimates" }),
    userPermissions: z
      .array(z.number())
      .optional()
      .meta({ description: "Permission IDs the current user has on this estimate" }),
  })
  .passthrough()
  .meta({ description: "Estimate data returned by the getEstimateData endpoint" });

export const getEstimateDataResponseSchema = z
  .object({
    status: z.boolean(),
    result: estimateDataSchema.optional(),
    errorMessage: z.string().optional(),
    errorCode: z.number().optional(),
  })
  .strip();

// ============================================================================
// Server-specific estimate schemas
// ============================================================================

const gridValueSchema = z.object({
  skip: z.number().int().nonnegative().optional().meta({ description: "Grid pagination offset (rows to skip)." }),
  take: z.number().int().positive().optional().meta({ description: "Grid page size (max rows to return)." }),
  sorted: z.array(sortConfig).optional().meta({ description: "Ordered column/direction sort specs for the grid." }),
  where: filterWhereClause.meta({
    description: "Grid filter clause (per-endpoint restricted variants override this with an allow-listed column set).",
  }),
  requiresCounts: z.boolean().optional().meta({ description: "If true, also return the total matching count." }),
});

const rateAnalysisSchema = z.object({
  itemId: z.string().optional(),
  unit: z.string().optional(),
  quantity: z.number().optional(),
  rate: z.number().optional(),
});

export const addFilesToEstimateRequest = z
  .object({
    // At least one file is required: an empty array returns {status:true} while doing nothing (and
    // may still create a wasteful copy-on-write micro-version for the no-op).
    fileVersionIds: z.array(objectIdStringSchema).min(1).meta({
      description:
        "Non-empty array of file version ObjectIds (24-hex) to attach, obtained from the files module; already-attached versions are skipped.",
    }),
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) to attach the files to.",
    }),
  })
  .meta({
    description:
      "Attach one or more BIM/BOQ file versions to an estimate's current version. Pass estimateId (from getEstimateList) and fileVersionIds (a non-empty array of file version ObjectIds from the files module). IFC files require BIM-takeoff addon access (else ADDONS_NO_ACCESS). Enforces RESOURCE_MODIFY and creates a FILE micro-version; already-attached versions are skipped.",
  });

export const addFilesToEstimateResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
    errorCode: z.number().optional().meta({ description: "Error code when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for add files to estimate",
  });

// List-only request: lists files attached to an estimate version, using `value` as a grid query.
// The former action="remove" branch is now the dedicated /removeFileFromEstimate endpoint.
export const getEstimateFilesRequest = z
  .object({
    value: gridValueSchema.extend({
      // Per-endpoint restricted `where` (do not mutate shared gridValueSchema).
      where: estimateFilesWhere.meta({
        description:
          "Grid filter clause restricted to the files-tab columns (fileName/addedDate/addedBy) plus per-project custom-field columns.",
      }),
      // Per-endpoint restricted `sorted` (do not mutate shared gridValueSchema). The files-tab grid's
      // sortable columns == ESTIMATE_FILES_FILTERABLE_FIELDS, and getEstimateFiles's only sort mapper
      // key (addedBy) is within it.
      sorted: sortConfigFor(ESTIMATE_FILES_FILTERABLE_FIELDS, { patterns: dynamicCustomFieldPatterns }).meta({
        description:
          "Grid sort restricted to the files-tab columns (fileName/addedDate/addedBy) plus per-project custom-field columns.",
      }),
      params: z.object({
        estimateId: objectIdStringSchema.meta({
          description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose attached files to list.",
        }),
        versionId: z.string().optional().meta({
          description:
            "Optional EstimateVersions _id; the version is resolved by estimateId+versionId, defaulting to the latest version.",
        }),
      }),
    }),
  })
  .optional()
  .meta({
    description:
      "List the files attached to an estimate version (aggregated from the files collection via the version's fileVersions[]), including each file's version, caching/conversion status and whether a newer file version is available (isFileUpdateRequired). Send value.params.estimateId (from getEstimateList) and optional versionId plus grid paging; the version is resolved by estimateId+versionId. Enforces RESOURCE_VIEW.",
  });

// Dedicated request for /removeFileFromEstimate (split out of the getEstimateFiles action-dispatcher).
// Both ids are fed to new ObjectId(...) by the service, so hex-validate them at the boundary.
export const removeFileFromEstimateRequest = z
  .object({
    fileVersionId: objectIdStringSchema.meta({
      description:
        "The attached file version's id (ObjectId 24-hex, from getEstimateFiles) to detach — send the id, not the file name.",
    }),
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) to detach the file from.",
    }),
  })
  .meta({
    description:
      "Detach a single file version from an estimate, removing it from both the current EstimateVersions.fileVersions[] and the Estimates.latestEstimateVersion embed. Pass estimateId (from getEstimateList) and fileVersionId (the attached file's id from getEstimateFiles). Enforces RESOURCE_MODIFY and creates a FILE micro-version. To attach files instead use addFilesToEstimate.",
  });

/**
 * Single estimate file row returned by getEstimateFiles. Aggregated from `files`
 * via $lookup on the EstimateVersions.fileVersions list. Fields that the BOQ files
 * grid consumes (FilesTab.tsx / EstimateProperty.tsx).
 */
export const estimateFileItemSchema = z
  .object({
    id: z.string().optional().meta({ description: "File version ID" }),
    fileName: z.string().optional(),
    fileDeleted: z.boolean().optional(),
    addedDate: z.union([z.string(), z.number()]).optional(),
    addedBy: z.object({ _id: z.string(), fullName: z.string().optional() }).optional(),
    version: z.string().optional(),
    spaceId: z.string().optional(),
    resourceType: z.string().optional(),
    cachingStatus: z.string().optional(),
    conversionStatus: z.string().optional(),
    isFileUpdateRequired: z.boolean().optional(),
    targetFileVersionId: z.string().optional(),
    wizardDetails: z
      .object({
        _id: z.string().optional(),
        quantityParameter: z.string().optional(),
        codeField: z.string().optional(),
        unitField: z.string().optional(),
      })
      .optional(),
    /** For merged-file resources only — constituent files' caching/conversion status. */
    sourceFileVersions: z
      .array(
        z.object({
          fileVersionId: z.string(),
          conversionStatus: z.string().optional(),
          cachingStatus: z.string().optional(),
        }),
      )
      .optional(),
  })
  .passthrough()
  .meta({ description: "Estimate file grid row" });

export const getEstimateFilesResponse = extendStatusResponse({
  result: z.array(estimateFileItemSchema).optional(),
  count: z.number().nonnegative().optional(),
  errorMessage: z.string().optional(),
}).meta({
  description: "Response for get estimate files",
});

// Controller uses CRUD pattern: action, value, key, params
// - action="insert": saves new item using params.estimateId, params.forceCorrectItemPlacement, and value (item data)
// - action="update": updates item using params.estimateId and value (item data)
// - action="remove": deletes item using key as itemId and params.estimateId
// - No action: gets items by spreading value to service
const estimateItemValueSchema = z.object({
  // Item data for insert/update. Per-field descriptions below become the JSON-Schema property docs
  // the MCP client shows the model; each is grounded in what saveEstimateItem/updateEstimateItem
  // (estimates.service.ts) actually does with the field.
  itemId: objectIdStringSchema.optional().meta({
    description:
      "Business-key ObjectId of a BOQ item. On INSERT, providing it copies that item from the estimate's LINKED cost database (DSR) into the BOQ (fails if the estimate has no cost database linked, or the id is not in it); OMIT it entirely to add a MANUAL free-text item (description + rate + quantity). Do NOT invent this id: a valid DSR itemId comes only from the estimate's linked cost-database listing (see getValidItems). On UPDATE it identifies the existing BOQ item to modify (get it from getEstimateItems).",
  }),
  parentId: parentIdStringSchema.nullable().optional().meta({
    description:
      'Parent item id in the BOQ tree = an EXISTING BOQ item\'s itemId (from getEstimateItems), or "0" for a top-level row (also the default when omitted/null). This is NOT the estimateId or the versionId — passing those returns "Parent item not found in the BOQ." Controls nesting and the item\'s serial order among its siblings.',
  }),
  description: z.string().optional().meta({
    description:
      "Free-text line description / item name. Used for MANUAL items; cost-database (DSR) items inherit it from the database.",
  }),
  // `code` is sent as `null` by the BOQ grid for header (non-leaf) items, so the
  // schema must allow null in addition to the string form.
  code: z.string().nullable().optional().meta({
    description:
      "Item code/reference — display/read field: like `unit`, it is NOT persisted by the insert/update save path (manual items don't store it; DSR items take their code from the cost database). The BOQ grid sends null for a header (group) row.",
  }),
  unit: z.string().optional().meta({
    description:
      "Display unit label only. The item's effective unit is derived from purchaseUnit + measurementUnit below — this string is NOT consumed by the insert/update save path.",
  }),
  level: z.number().int().nonnegative().optional().meta({
    description:
      "The item's depth in the BOQ tree (root items are level 0). Defense-in-depth bound only: the service now DERIVES the stored depth from the resolved parent chain and ignores this value, so a spoofed level cannot desync a row's indentation from its real nesting.",
  }),
  quantity: z.number().nonnegative().nullable().optional().meta({
    description:
      "Quantity for a LEAF item (amount = quantity × rate). Forced to null for a header row (hasChild=true), whose amount is rolled up from its children.",
  }),
  rate: z.number().nonnegative().nullable().optional().meta({
    description:
      "Unit rate for a MANUAL leaf item. Cost-database (DSR) items derive their rate from the database, so this is ignored for them.",
  }),
  additionalAmount: z.number().nonnegative().nullable().optional().meta({
    description: "Flat extra amount added on top of the item's computed total. Applied on UPDATE (not read on insert).",
  }),
  // Write-side: ObjectId string of the PurchaseUnits document. The BOQ grid
  // extracts `.id` from its populated read-side `{ id, name }` object before
  // calling save/update (see EstimateDsrItemsGridNew.tsx lines 817, 832, 1277).
  // The service then constructs `new ObjectId(purchaseUnit)` (estimates.service.ts:1657).
  // ObjectId strings the service feeds to `new ObjectId(...)` (throws on non-hex).
  purchaseUnit: objectIdStringSchema.nullable().optional().meta({
    description:
      "ObjectId of a PurchaseUnits document — send the id, NOT the display name (the grid extracts .id from its {id,name}). Sets the item's purchase unit.",
  }),
  measurementUnit: objectIdStringSchema.nullable().optional().meta({
    description:
      "ObjectId of a MeasurementUnits document (send the id, not the name). Sets the measurement unit; applied together with conversionRate on UPDATE.",
  }),
  conversionRate: z.string().optional().meta({
    description:
      "Conversion ratio (as a string) between the purchase unit and the measurement unit; applied with measurementUnit on UPDATE.",
  }),

  HSN_SAC: z.string().optional().meta({ description: "HSN/SAC tax-classification code (India GST) for the item." }),
  rateAnalysis: z.unknown().optional().meta({
    description:
      "Rate-analysis breakdown (labour / material / sub-components). Stored as-is and forwarded to S3 — its shape is not validated; send the structure the rate-analysis UI produces.",
  }),
  isRateAnalysisEnable: z.boolean().optional().meta({
    description:
      "Whether the item's rate is driven by its rateAnalysis breakdown rather than a flat rate. Applied on UPDATE.",
  }),
  rangeUnitValue: z.number().optional().meta({
    description:
      "For a variable-rate (range-priced) cost-database item, the range value that selects which rate band applies.",
  }),
  costIndex: z.number().optional().meta({
    description:
      "Cost index (price-adjustment factor). On UPDATE of a cost-database item a positive value recalculates the rate; on INSERT the estimate version's costIndex is used and this field is ignored.",
  }),
  // Grid params for read (list endpoint only)
  skip: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .meta({ description: "Grid pagination offset — LIST read (getEstimateItems) only; ignored by insert/update." }),
  take: z
    .number()
    .int()
    .positive()
    .optional()
    .meta({ description: "Grid page size — LIST read only; ignored by insert/update." }),
  sorted: z
    .array(sortConfig)
    .optional()
    .meta({ description: "Grid sort spec — LIST read only; ignored by insert/update." }),
  where: estimateItemsWhere.meta({
    description: "Grid filter clause (allow-listed columns) — LIST read only; ignored by insert/update.",
  }),
  requiresCounts: z
    .boolean()
    .optional()
    .meta({ description: "If true, the LIST read also returns a total count. LIST read only." }),
  params: z
    .object({
      estimateId: objectIdStringSchema
        .optional()
        .meta({ description: "(list) Estimate _id to list items for (from getEstimateList)." }),
      // versionId/takeOffItemId/units are fed to new ObjectId() in the service (estimateHelper
      // :1360/:1367, estimates.redis:18) — hex-validate so a non-hex string 400s instead of BSONError.
      versionId: objectIdStringSchema.optional().meta({
        description: "(list) Specific EstimateVersions _id to read; defaults to the estimate's latest version.",
      }),
      parentId: z.string().optional().meta({ description: "(list) Return only the children of this parent item id." }),
      getAllItems: z
        .boolean()
        .optional()
        .meta({ description: "(list) Return the full item tree instead of a single level." }),
      getDsrItemsOnly: z
        .boolean()
        .optional()
        .meta({ description: "(list) Return only cost-database-linked (DSR) items." }),
      getChildrenOnly: z
        .boolean()
        .optional()
        .meta({ description: "(list) Return only the direct children of the given parentId." }),
      modifyOutputForSummary: z
        .boolean()
        .optional()
        .meta({ description: "(list) Reshape the output for the estimate summary view." }),
      takeOffAttachedItems: z
        .boolean()
        .optional()
        .meta({ description: "(list) Include items that are attached to takeoffs." }),
      takeOffItemId: objectIdStringSchema
        .optional()
        .meta({ description: "(list) Filter to items linked to this takeoff item id." }),
      taskId: z.string().optional().meta({ description: "(list) Scope the read to a contract task context." }),
      formId: z
        .string()
        .optional()
        .meta({ description: "(list) Scope the read to a form (InspectionAndProgress) context." }),
      units: objectIdStringSchema
        .optional()
        .meta({ description: "(list) MeasurementUnits _id used for unit conversion in the read output." }),
    })
    .optional()
    .meta({
      description:
        "Query params for the LIST read (getEstimateItems) — selects the estimate/version and which item subset to return. NOT used by insert/update, which take the estimate id in the TOP-LEVEL `params.estimateId`.",
    }),
});

// List-only request: lists BOQ items for an estimate version, using `value` as a grid query.
// The former insert/update/remove branches are now the dedicated /insertEstimateItem,
// /updateEstimateItem and /deleteEstimateItem endpoints.
export const getEstimateItemsRequest = z
  .object({
    value: estimateItemValueSchema.optional(),
  })
  .meta({
    description:
      "List the BOQ (Bill of Quantities) line items of an estimate's latest (or a given) version, as a paginated/filterable grid; each row carries itemId, description, code, quantity, rate and amount. Pass the query under `value` with `value.params.estimateId` (from getEstimateList) and optional `value.params.versionId`; enforces RESOURCE_VIEW. Read-only companion to the item mutation endpoints; merge estimates return combined constituent rows.",
  });

// Top-level `params` shape shared by the item list + mutation endpoints.
const estimateItemsTopParamsShape = {
  estimateId: objectIdStringSchema.optional().meta({
    description:
      "The estimate's _id (from getEstimateList) — identifies which estimate to insert/update/delete the item in. Required in practice for the mutation endpoints (an absent id degrades to the not-found path).",
  }),
  versionId: z.string().optional().meta({
    description:
      "Ignored by the insert/update/delete endpoints — they always operate on the estimate's latest draft version (there is no version targeting here).",
  }),
  forceCorrectItemPlacement: z.boolean().optional().meta({
    description:
      "INSERT only: if true, auto-corrects a cost-database (DSR) item's position when the supplied parentId/level is inconsistent with its cost-database parent hierarchy.",
  }),
  getAllItems: z.boolean().optional().meta({
    description:
      "Not read by any endpoint (the list reads value.params.getAllItems); present only for shape compatibility.",
  }),
  parentId: z.string().optional().meta({
    description:
      "Not read by any endpoint (the list reads value.params.parentId); present only for shape compatibility.",
  }),
};
const estimateItemsTopParamsSchema = z.object(estimateItemsTopParamsShape);

// Dedicated request schemas for the item mutation endpoints (split from the getEstimateItems
// action-dispatcher). insert/update carry the BOQ item `value` + `params.estimateId`; delete
// carries `key` (itemId or itemId[]) + `params.estimateId`. Each maps 1:1 to one service call.
export const insertEstimateItemRequest = z
  .object({
    value: estimateItemValueSchema,
    params: estimateItemsTopParamsSchema.optional(),
  })
  .meta({
    description:
      "Add ONE BOQ line item to an estimate's current draft version, inside a transaction (RESOURCE_MODIFY). Send the item under `value` plus `params.estimateId` (from getEstimateList): with a `value.itemId` it inserts a cost-database (DSR) item copied from the estimate's linked cost database (fails if none is linked or the item is not found there); with no itemId it inserts a manual item (description/quantity/rate/unit). Use parentId \"0\" for a top-level row; call once per line. This is the web BOQ insert path (prefer over saveEstimateItem).",
  });

export const updateEstimateItemRequest = z
  .object({
    value: estimateItemValueSchema,
    params: estimateItemsTopParamsSchema.optional(),
  })
  .meta({
    description:
      "Update an existing BOQ line item (rate, quantity, unit, description, HSN/SAC, etc.) resolved by `value.itemId` within the estimate's current version, then recompute amounts up the tree; runs in a transaction and enforces RESOURCE_MODIFY. Pass the changed fields under `value` and `params.estimateId` (from getEstimateList); obtain itemId from getEstimateItems.",
  });

export const deleteEstimateItemRequest = z
  .object({
    // itemId (or itemId[]) fed to new ObjectId(...) by deleteEstimateItem — hex-validate at the boundary
    // and require it so a keyless delete cannot mint a random ObjectId that silently no-ops.
    key: z.union([objectIdStringSchema, z.array(objectIdStringSchema)]).meta({
      description:
        "The BOQ item's itemId (ObjectId 24-hex) to delete, or an array of itemIds for a bulk delete (from getEstimateItems). Each item's descendants are removed too. Send the itemId, not the description.",
    }),
    params: estimateItemsTopParamsSchema.optional(),
  })
  .meta({
    description:
      "Delete one or more BOQ line items (and all their descendants) from the estimate's current version, decrementing sibling serials; runs in a transaction and enforces RESOURCE_MODIFY. Pass the item id(s) as `key` (a single itemId string or an array) plus `params.estimateId` (from getEstimateList); obtain itemId(s) from getEstimateItems.",
  });

/**
 * BOQ items returned from a Mongo aggregate. Each entry is the WIRE-FORMAT shape:
 * a serialized read-side projection of `estimateItemDocument` (collection SSOT)
 * with ObjectIds rendered as strings and joined fields (purchaseUnit name, measurementUnit
 * name/factor, parentsList, hasChild, etc.) attached by the aggregation. Uses `looseObject`
 * so UI-side row extensions (e.g. `__isDummy`, `expanded`, contract-link flags) pass through.
 *
 * Frontend types should derive via `NonNullable<GetEstimateItemsResponse["result"]>[number]`.
 */
const estimateItemsResultEntrySchema = z.looseObject({
  itemId: z.string(),
  _id: z.string().optional(),
  estimateId: objectIdStringSchema.optional(),
  estimateVersionId: z.string().optional(),
  versionId: z.string().optional(),
  /** "ESTIMATE" | "DSR" */
  type: z.string().optional(),
  status: z.string().optional(),
  /** "0" = root level; otherwise the itemId of the parent */
  parentId: z.string().nullable().optional(),
  level: z.number().nullable().optional(),
  itemLevel: z.number().optional(),
  serial: z.number().optional(),
  hasChild: z.boolean().optional(),
  description: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  HSN_SAC: z.string().nullable().optional(),
  amount: z.number().nullable().optional(),
  additionalAmount: z.number().optional(),
  rate: z.union([z.number(), z.string()]).nullable().optional(),
  quantity: z.number().nullable().optional(),
  quantityFromTakeoffs: z.number().nullable().optional(),
  manualQuantity: z.number().nullable().optional(),
  numberingSystem: z.number().optional(),
  // createdDate is stored as a Unix-ms number in MongoDB; some aggregations serialize it
  // as an ISO string (legacy summary endpoint). Accept both at the API boundary.
  createdDate: z.union([z.number(), z.string()]).optional(),
  updatedDate: z.union([z.number(), z.string()]).optional(),
  parentsList: z
    .array(
      z.object({
        itemId: z.string(),
        type: z.string().nullable(),
      }),
    )
    .optional(),
  purchaseUnit: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
    })
    .nullable()
    .optional(),
  measurementUnit: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
      relation: z.string().optional(),
      factor: z.number().optional(),
    })
    .nullable()
    .optional(),
});

export type EstimateItemsResultEntry = z.infer<typeof estimateItemsResultEntrySchema>;

export const getEstimateItemsResponse = extendStatusResponse({
  result: z.array(estimateItemsResultEntrySchema).optional(),
  count: z.number().nonnegative().optional(),
});

// Service getEstimateItemsRemote expects: { skip, take, params }
// where params contains { estimateId, versionId }
export const getEstimateItemsRemoteRequest = z
  .object({
    skip: z.number().int().nonnegative().optional().meta({ description: "Pagination offset (items to skip)." }),
    take: z.number().int().positive().optional().meta({ description: "Page size (max items to return)." }),
    params: z.object({
      // ObjectId-format validation: the service passes these straight to `new ObjectId(...)`,
      // which throws on a non-hex string. objectIdString enforces the 24-char hex contract.
      estimateId: objectIdString.meta({
        description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose items to fetch.",
      }),
      versionId: objectIdString
        .optional()
        .meta({ description: "Optional EstimateVersions _id; omitted uses the estimate's latest version." }),
    }),
  })
  .meta({
    description:
      "Fetch a flat list of ALL BOQ items for an estimate version (paginated by skip/take, no tree/filter grid), tagging each row with itemType 'DSR' (has a code) or 'MARKET RATE'; enforces RESOURCE_VIEW. Requires `params.estimateId` (from getEstimateList) and optional `params.versionId`. Simplified export/remote read; use getEstimateItems for the interactive grid.",
  });

export const getEstimateItemsRemoteResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    result: z.array(z.record(z.string(), z.unknown())).optional().meta({ description: "List of estimate items" }),
    count: z.number().nonnegative().optional().meta({ description: "Total count of items" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for get estimate items remote",
  });

export const getEstimateItemRequest = z
  .object({
    // ObjectId-format validation: itemId/estimateId/versionId are fed to `new ObjectId(...)`
    // in getEstimateItem; a non-hex string throws BSONError. Enforce the hex contract here.
    versionId: objectIdString.optional().meta({
      description:
        "Optional EstimateVersions _id (from getEstimateVersionsLatest); a versionId not belonging to estimateId resolves to nothing. Omitted uses the latest version.",
    }),
    itemId: objectIdString.meta({
      description: "The BOQ item's business-key itemId (ObjectId 24-hex, from getEstimateItems) to fetch.",
    }),
    estimateId: objectIdString.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) the item belongs to.",
    }),
  })
  .meta({
    description:
      "Fetch a single BOQ line item in full detail, resolved by itemId within a specific estimateId + versionId (a versionId not belonging to that estimate resolves to nothing); enforces RESOURCE_VIEW. Get estimateId from getEstimateList, itemId from getEstimateItems, and optional versionId from getEstimateVersionsLatest. Use this for one item; use getEstimateItems for the list.",
  });

export const getEstimateItemResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    data: z.record(z.string(), z.unknown()).optional().meta({ description: "The estimate item data" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for get estimate item",
  });

// Controller expects: value.requiresCounts, value.skip, value.take, value.where[0].value (parentId), value.params
// Service fetchEstimateItems receives: { requiresCounts, skip, take, ...value.params, parentId }
const childEstimateItemsValueSchema = z.object({
  skip: z.number().int().nonnegative().optional(),
  take: z.number().int().positive().optional(),
  requiresCounts: z.boolean().optional(),
  sorted: z.array(sortConfig).optional(),
  where: z
    .array(
      z.object({
        field: z.string().optional(),
        operator: z.string().optional(),
        value: z.string(), // parentId is extracted from here
      }),
    )
    .optional(),
  params: z
    .object({
      estimateId: objectIdStringSchema.optional(),
      versionId: z.string().optional(),
    })
    .optional(),
});

export const getChildEstimateItemsResponse = statusResponse.meta({
  description: "Response for get child estimate items",
});

// List-only request: lists additional cost items for an estimate version, using `value` as a
// grid query. The former action="remove" branch is now the dedicated /deleteAdditionalCost endpoint.
export const getAdditionalCostRequest = z
  .object({
    value: gridValueSchema
      .extend({
        estimateId: objectIdStringSchema.optional().meta({
          description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose additional costs to list.",
        }),
        versionId: z.string().optional().meta({
          description:
            "Optional EstimateVersions _id; the version is resolved by estimateId+versionId, and a foreign/unknown id yields an empty result.",
        }),
      })
      .optional(),
  })
  .meta({
    description:
      "List an estimate's additional-cost line items (the EstimateVersions.additionalCost[] entries — e.g. taxes/overheads added on top of the BOQ), paginated. Send value as a grid query carrying estimateId (from getEstimateList) and optionally versionId (from getEstimateVersion/getEstimateVersionsLatest); the version is resolved by estimateId+versionId and a foreign/unknown versionId yields an empty result. Enforces RESOURCE_VIEW. These are estimate additional costs, NOT the portal-level charges returned by chargesList.",
  });

// Dedicated request for /deleteAdditionalCost (split out of the getAdditionalCost action-dispatcher).
// `estimateId` is fed to new ObjectId(...) by deleteAdditionalCost, so hex-validate it at the boundary.
export const deleteAdditionalCostRequest = z
  .object({
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose additional costs to delete.",
    }),
    uniqueIds: z.array(z.string()).min(1).meta({
      description:
        "Non-empty array of the additional-cost items' uniqueId values (from getAdditionalCost) to remove — the uniqueId, not the name.",
    }),
  })
  .meta({
    description:
      "Delete one or more additional-cost items from an estimate's current version, matched by their uniqueId. Pass estimateId (from getEstimateList) plus uniqueIds (the uniqueId values returned by getAdditionalCost); at least one is required. Enforces RESOURCE_MODIFY and creates an ADDITIONAL-COST micro-version. This removes estimate additional costs, not portal charges (see deleteCharge).",
  });

export const getAdditionalCostResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    result: z
      .array(z.record(z.string(), z.unknown()))
      .optional()
      .meta({ description: "List of additional cost items" }),
    count: z.number().nonnegative().optional().meta({ description: "Total count of items" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for get additional cost",
  });

/** Reference item for additional cost calculation */
export const additionalCostReferenceSchema = z.object({
  id: z.string().meta({ description: "Reference item ID" }),
  name: z.string().meta({ description: "Reference item name" }),
  value: z.number().meta({ description: "Reference value" }),
  variable: z.string().optional().meta({ description: "Variable name used in formula" }),
  serial: z.number().meta({ description: "Serial order" }),
});

/**
 * Shape of a single additional cost item as returned by the getAdditionalCost API.
 * Extends the save-request fields with server-generated fields (_id, id, serial, etc.).
 */
export const additionalCostItemSchema = z
  .object({
    _id: z.string().optional().meta({ description: "MongoDB document ID" }),
    id: z.string().optional().meta({ description: "Alias for _id used by some endpoints" }),
    uniqueId: z.string().optional().meta({ description: "Client-facing unique identifier" }),
    estimateId: z.string().optional().meta({ description: "Parent estimate ID" }),
    name: z.string().optional().meta({ description: "Name of the additional cost item" }),
    value: z.number().optional().meta({ description: "Computed or manual value" }),
    formula: z.string().optional().meta({ description: "Formula used to calculate the value" }),
    serial: z.number().optional().meta({ description: "Display order" }),
    references: z
      .array(additionalCostReferenceSchema)
      .optional()
      .meta({ description: "Reference items used in formula" }),
    isDeleted: z.boolean().optional().meta({ description: "Soft-delete flag" }),
    createdDate: z.number().optional().meta({ description: "Creation timestamp (epoch ms)" }),
  })
  .meta({ description: "A single additional cost item returned by the API" });

export const saveAdditionalCostRequest = z
  .object({
    estimateId: objectIdString.meta({
      description: "The estimate ID to add/update additional cost",
      example: "64b2c123df890abc1234abcd",
    }),
    name: zPlainTextRequired.meta({
      description: "Name of the additional cost item",
      example: "Tax",
    }),
    value: z.number().meta({
      description: "Value of the additional cost",
      example: 1500.5,
    }),
    uniqueId: z.string().optional().meta({
      description: "Unique ID for updating existing additional cost (omit for new)",
    }),
    references: z.array(additionalCostReferenceSchema).optional().meta({
      description: "Reference items used in formula calculation",
    }),
    formula: z.string().optional().meta({
      description: "Formula for calculating the value",
      example: "A + B * 0.1",
    }),
  })
  .meta({
    description:
      "Create or update a single additional-cost line item stored in EstimateVersions.additionalCost[]. Pass estimateId (from getEstimateList), a name and a numeric value; to update an existing item pass its uniqueId (from getAdditionalCost) — omit uniqueId to add a new one. Optional formula/references drive computed values. Enforces RESOURCE_MODIFY.",
  });

export const saveAdditionalCostResponse = z
  .object({
    status: z.boolean().meta({
      description: "Indicates whether the operation was successful",
    }),
    errorMessage: z.string().optional().meta({
      description: "Error message when status is false",
    }),
  })
  .strip()
  .meta({
    description: "Response for save additional cost",
  });

export const getEstimateAdditionalCostTotalRequest = z
  .object({
    // ObjectId-format validation: estimateId reaches `new ObjectId(...)` via getEstimateData
    // and versionId via fetchLatestEstimateVersion; both throw on a non-hex string.
    estimateId: objectIdString.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) to total additional costs for.",
    }),
    versionId: objectIdString.optional().meta({
      description:
        "Optional EstimateVersions _id (resolved with estimateId); omitted uses the latest version. A version with no additional costs returns 0.",
    }),
  })
  .meta({
    description:
      "Return the summed total (a single number) of all additional-cost values on the estimate's version. Pass estimateId (from getEstimateList) and optionally versionId (resolved with estimateId); a version with no additional costs returns 0. Enforces RESOURCE_VIEW. Distinct from getEstimateTotal, which returns the BOQ items amount, not the additional-cost sum.",
  });

export const getEstimateAdditionalCostTotalResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    data: z.number().nonnegative().optional().meta({ description: "The total additional cost amount" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for get estimate additional cost total",
  });

export const getEstimateListRequest = getEstimateListRequestSchema.meta({
  description:
    "List estimates visible to the current user in a space/project/portal — returns each estimate's id, name, counter, amount, costDatabase and version metadata. Scope it via params.spaceId (from projects_getSpacesDataByProjectId) or params.projectId (from getIncludedProjectsController) or params.estimateId; with no scope it lists the whole portal. Only estimates where the user holds RESOURCE_VIEW are returned. Use it to resolve an estimate NAME to its id before reading or editing. Send the query either bare or wrapped as {value:<query>, params}; set getEstimatesWithNamedVersion=true to fold in published-version rows.",
});

// Dedicated request schemas for the estimate mutation endpoints (split from the getEstimateList
// action-dispatcher). Each maps 1:1 to a service call: createEstimate / updateEstimateData /
// deleteEstimate. The body is the estimate `value` fields directly (no action/key envelope).
export const createEstimateRequest = estimateListInsertValueSchema.meta({
  description:
    "Create a NEW standard estimate in a space. Requires spaceId (from projects_getSpacesDataByProjectId), projectId (from getIncludedProjectsController) and a non-empty name; optional description, costIndex (must be >0), dsrId to link a cost database, and inheritPermission. Enforces create permission on the space for the ESTIMATES module (checkCreatePermissionInSpace), not RESOURCE_MODIFY. Use this to start a fresh estimate — NOT createEstimateVersion (which only snapshots an existing one) and NOT createMergeEstimate (which merges several).",
});

export const updateEstimateDataRequest = estimateListUpdateValueSchema.meta({
  description:
    "Update an existing estimate's metadata: rename (name), set/clear description, set costIndex (>0), and optionally upgrade the linked DSR/cost-database to its latest published version (upgradeDsrVersion, with forceDeletedItemToManual). Pass _id = the estimate id (from getEstimateList). Enforces RESOURCE_MODIFY. An empty/whitespace name is a silent no-op; description=null clears it. Runs inside a transaction and creates a micro-version. Does not change display units — use changeDisplayUnit for that.",
});

export const deleteEstimateRequest = z
  .object({
    _id: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) to soft-delete — the id, not the name.",
    }),
  })
  .meta({
    description:
      "Soft-delete an estimate by _id (from getEstimateList): sets the estimate's status to 'deleted', cascades the deleted status onto its EstimateVersions/EstimateItems/TakeOffs/Measurements, and removes it from the file system. Enforces RESOURCE_DELETE. The record is not physically removed.",
  });

export const getEstimateListResponse = getEstimateListResponseSchema.meta({
  description: "Response for get estimate list",
});

export const getCpwdEstimateListRequest = z
  .object({
    projectCode: z.string().meta({
      description:
        "The project's human CPWD project code (the project's projectId string, e.g. from a project record's projectId), NOT an ObjectId; resolved against projectCode + portal.",
    }),
    skip: z.number().int().nonnegative().optional().meta({ description: "Pagination offset (estimates to skip)." }),
    take: z.number().int().positive().optional().meta({ description: "Page size (max estimates to return)." }),
  })
  .meta({
    description:
      "List an individual project's estimates by the project's human CPWD project CODE (projectCode, e.g. the project's projectId string) rather than an ObjectId, with skip/take paging. Resolves the project by projectCode+portal, then returns that project's estimates with named-version data. Use this only when you have the project code; otherwise use getEstimateList with a projectId.",
  });

export const getCpwdEstimateListResponse = statusResponse.meta({
  description: "Response for get CPWD estimate list",
});

export const changeDisplayUnitRequest = z
  .object({
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose display unit to change.",
    }),
    column: z.string().optional().meta({
      description:
        "IGNORED by the service: it is destructured out but never read. The dimension to change is inferred from whichever of linearDisplayUnit/areaDisplayUnit/volumeDisplayUnit/countDisplayUnit is present.",
    }),
    changeAllMeasurements: z.boolean().optional().meta({
      description:
        "IGNORED by the service: destructured out but never read; it does not affect the update. Measurements matching the changed unit's type are always re-synced.",
    }),
    // The service (updateEstimateData) reads these four and feeds each to new ObjectId(); define and
    // hex-validate them so a non-hex value 400s instead of slipping through .passthrough() into a
    // BSONError crash.
    linearDisplayUnit: objectIdStringSchema.optional().meta({
      description:
        "MeasurementUnits ObjectId (24-hex, send the id not the name) to set as the linear display unit. Send exactly one of the four *DisplayUnit fields — the first present (in order linear, area, volume, count) is applied.",
    }),
    areaDisplayUnit: objectIdStringSchema.optional().meta({
      description:
        "MeasurementUnits ObjectId (24-hex) to set as the area display unit. Send exactly one *DisplayUnit field per call.",
    }),
    volumeDisplayUnit: objectIdStringSchema.optional().meta({
      description:
        "MeasurementUnits ObjectId (24-hex) to set as the volume display unit. Send exactly one *DisplayUnit field per call.",
    }),
    countDisplayUnit: objectIdStringSchema.optional().meta({
      description:
        "MeasurementUnits ObjectId (24-hex) to set as the count display unit. Send exactly one *DisplayUnit field per call.",
    }),
  })
  .passthrough()
  .meta({
    description:
      "Change the display unit for one measurement dimension of an estimate (send estimateId plus exactly one of linearDisplayUnit/areaDisplayUnit/volumeDisplayUnit/countDisplayUnit as a MeasurementUnits ObjectId), optionally applying it across all measurements (changeAllMeasurements). Enforces RESOURCE_MODIFY; internally updates the estimate and recomputes affected measurement quantities. estimateId from getEstimateList.",
  });

export const changeDisplayUnitResponse = statusResponse.meta({
  description: "Response for change display unit",
});

export const saveEstimateItemRequest = z
  .object({
    estimateId: objectIdString.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) to add the item to.",
    }),
    itemId: objectIdString.meta({
      description:
        "Business-key ObjectId (24-hex) of the item. Present = copy this item from the estimate's linked cost database (DSR) into the BOQ (fails if no DSR is linked or the id is not in it); to add a manual item use the /insertEstimateItem endpoint (this flat endpoint requires itemId).",
      example: "64b2c123df890abc1234abcd",
    }),
    parentId: parentIdStringSchema.optional().meta({
      description:
        'Parent item\'s itemId in the BOQ tree, or "0" for a top-level row (default). Controls nesting/serial order.',
    }),
    name: z.string().optional().meta({
      description: "IGNORED by saveEstimateItem (not destructured/read). Use `description` for the item text.",
    }),
    description: z.string().optional().meta({
      description:
        "Free-text line description for a manual item; cost-database (DSR) items inherit their description from the database.",
    }),
    code: z.string().optional().meta({
      description:
        "IGNORED by saveEstimateItem (not read/persisted); DSR items take their code from the cost database.",
    }),
    unit: z.string().optional().meta({
      description:
        "IGNORED by saveEstimateItem (not read); the effective unit derives from purchaseUnit/measurementUnit set via saveItemDetails.",
    }),
    quantity: z.number().nonnegative().optional().meta({
      description:
        "Quantity for a leaf item (amount = quantity x rate); a header item (hasChild=true) rolls up its children instead. Non-negative (matches the /insertEstimateItem contract) so a negative line cannot understate the BOQ total.",
    }),
    rate: z.number().nonnegative().optional().meta({
      description:
        "Unit rate for a manual leaf item (non-negative); DSR items derive their rate from the cost database.",
    }),
    // These are read by saveEstimateItem but were previously stripped by validation, so a caller
    // could not create a header/group item (hasChild), set the tree level, supply a range, or opt
    // into auto-correction. Types mirror estimateItemValueSchema / the getEstimateItems params.
    hasChild: z.boolean().optional().meta({
      description:
        "If true, insert the item as a header/group node (its amount is rolled up from children rather than quantity x rate).",
    }),
    level: z
      .number()
      .optional()
      .meta({ description: "The item's depth in the BOQ tree (root = 0); passed through to item placement." }),
    rangeUnitValue: z.number().optional().meta({
      description:
        "For a variable-rate (range-priced) cost-database item, the range value selecting which rate band applies.",
    }),
    forceCorrectItemPlacement: z.boolean().optional().meta({
      description:
        "If true, auto-correct a DSR item's position when the supplied parentId/level conflicts with its cost-database parent hierarchy.",
    }),
  })
  .meta({
    description:
      "Alternate flat-payload endpoint to add a single BOQ item, calling the same saveEstimateItem service as /insertEstimateItem. This flat endpoint REQUIRES itemId, so it only adds a cost-database/DSR item (RESOURCE_MODIFY); for a MANUAL item use /insertEstimateItem. Fields are sent at the top level (estimateId, itemId, description, quantity, rate, parentId, hasChild) rather than under value/params. Prefer /insertEstimateItem for the web BOQ insert flow; use this only where the flat contract is expected.",
  });

export const saveEstimateItemResponse = statusResponse.meta({
  description: "Response for save estimate item",
});

export const changeItemTypeInEstimateRequest = z
  .object({
    itemId: objectIdStringSchema.meta({
      description:
        "The coded (DSR) BOQ item's itemId (ObjectId 24-hex, from getEstimateItems) to convert to manual. Refused if it has any coded descendant.",
    }),
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) the item belongs to.",
    }),
  })
  .meta({
    description:
      'Converts a coded cost-database (DSR) BOQ item into a manual estimate item — clears its code, sets type to "ESTIMATE", and mints a new itemId (returned as currentItemId). This is a one-way DSR→manual conversion, NOT a header/leaf toggle. Refuses if the item has any coded (cost-database) descendant. Runs in a transaction, requires RESOURCE_MODIFY. Pass estimateId (from getEstimateList) and itemId (from getEstimateItems).',
  });

export const changeItemTypeInEstimateResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    data: z
      .object({
        currentItemId: z.string().meta({ description: "The new item ID after type change" }),
      })
      .optional()
      .meta({ description: "Response data on success" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for change item type in estimate",
  });

export const createEstimateVersionRequest = z
  .object({
    estimateId: objectIdStringSchema.optional().meta({
      description:
        "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose current draft to publish as the next named version.",
    }),
  })
  .meta({
    description:
      'Publishes the current auto-save draft of an EXISTING estimate as the next named version (sets versionNumber = current max + 1); if a "Default Estimates Review Flow" is configured for the estimate\'s space it also auto-creates a default review. Pass estimateId (from getEstimateList). Requires RESOURCE_UPLOAD permission on the estimate. Distinct from createEstimate (which makes a brand-new estimate) and restoreEstimateVersion (which restores an old version).',
  });

export const createEstimateVersionResponse = statusResponse.meta({
  description: "Response for create estimate version",
});

export const getEstimateVersionRequest = z
  .object({
    // Required: getEstimateVersion feeds estimateId to getEstimateData and new ObjectId(estimateId).
    // An empty body previously passed validation and produced a misleading "Estimate not found".
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose version history to fetch.",
    }),
  })
  .meta({
    description:
      "Returns the full version history of one estimate, split into namedVersions (published versions grouped by version number, each group newest-first) and microVersions (unnamed auto-save drafts, newest-first). Pass estimateId (from getEstimateList). Requires RESOURCE_VIEW permission. Use this for the complete history view; use getEstimateVersionsLatest for a compact version-picker list.",
  });

/** Version note schema for estimate version */
const versionNoteSchema = z.object({
  description: z.string().meta({ description: "Note content/description" }),
  updatedBy: z.string().meta({ description: "User ID who updated the note" }),
  updatedOn: z.number().meta({ description: "Timestamp when note was updated" }),
});

/** Version entry schema for estimate version response */
export const estimateVersionEntrySchema = z.object({
  _id: z.string().meta({ description: "Version document ID" }),
  dateVersion: z.number().meta({ description: "Date version timestamp" }),
  updatedBy: z.object({ _id: z.string() }).meta({ description: "User who updated this version" }),
  versionNumber: z.number().meta({ description: "Version number" }),
  estimateType: z.string().optional().meta({ description: "Type of estimate version" }),
  note: versionNoteSchema.optional().meta({ description: "Version note" }),
});

export const getEstimateVersionResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    namedVersions: z
      .array(z.array(estimateVersionEntrySchema))
      .optional()
      .meta({ description: "Named/major versions grouped by version number" }),
    microVersions: z.array(estimateVersionEntrySchema).optional().meta({ description: "Micro/auto-save versions" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for get estimate version",
  });

export const getEstimateVersionsLatestRequest = z
  .object({
    // ObjectId-format validation: getEstimateVersionsLatest passes estimateId to
    // `new ObjectId(...)`, which throws on a non-hex string.
    estimateId: objectIdString.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose version-picker list to fetch.",
    }),
  })
  .meta({
    description:
      'Returns a compact list of the latest snapshot per version number for one estimate — each entry is { _id, versionNumber } where versionNumber is a label like "v3" or "Draft" — intended for a version-picker dropdown. Pass estimateId (from getEstimateList). Requires RESOURCE_VIEW permission. Unlike getEstimateVersion this returns only the single most-recent doc per version number, not the full grouped history.',
  });

export const getEstimateVersionsLatestResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    result: z
      .array(
        z.object({
          _id: z.string().meta({ description: "Estimate version ID" }),
          versionNumber: z.string().meta({ description: "Version label e.g. 'v1' or 'Draft'" }),
        }),
      )
      .optional()
      .meta({ description: "List of latest estimate versions" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
  })
  .meta({
    description: "Response for get latest estimate versions",
  });

export const updateManualMeasurementSheetResponse = statusResponse.meta({
  description: "Response for update manual measurement sheet",
});

export const restoreEstimateVersionRequest = z
  .object({
    estimateId: objectIdStringSchema.optional().meta({
      description:
        "Optional estimate _id (ObjectId 24-hex, from getEstimateList); derived from the target version's owner if omitted. If supplied it MUST match that owner or the call is rejected (IDOR guard).",
    }),
    restoreEstimateVersionId: objectIdStringSchema.meta({
      description:
        "The version _id (EstimateVersions _id, ObjectId 24-hex, from getEstimateVersion/getEstimateVersionsLatest) to restore as the new working draft.",
    }),
  })
  .meta({
    description:
      "Restores a previous estimate version as the new working draft: it first publishes the current draft as a named version, then copies the target version's items/takeoffs/measurements into a fresh draft (versionNumber null, activity RESTORED) and repoints the estimate's latest version at it. Pass restoreEstimateVersionId (a version _id from getEstimateVersion/getEstimateVersionsLatest); estimateId is optional and derived from the target version's owner — if supplied it must match that owner or the call is rejected (IDOR guard). Requires RESOURCE_MODIFY permission.",
  });

export const restoreEstimateVersionResponse = statusResponse.meta({
  description: "Response for restore estimate version",
});

export const updateCostDatbaseInEstimateRequest = z
  .object({
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose cost database to change.",
    }),
    // null = unlink the cost database; otherwise it is fed to new ObjectId(dsrId).
    dsrId: objectIdStringSchema.nullable().meta({
      description:
        "Cost-database (DSR) ObjectId (24-hex, from the cost-database/DSR list) to link/change to, or null to UNLINK. A no-op if it already matches; unlinking is refused if an attached file uses a takeoff wizard.",
    }),
  })
  .meta({
    description:
      'Links, changes, or unlinks the cost database (DSR) for an estimate: pass estimateId (from getEstimateList) and dsrId (a cost-database ObjectId, or null to unlink). On change it creates a COSTDATABASE-UPDATE version, resets costIndex to 1, and CONVERTS the estimate\'s existing DSR-linked items into manual items (detaches/freezes them — it does NOT re-price them against the new database); a no-op when the dsrId already matches, and unlinking is refused if an attached file uses a takeoff wizard. Runs in a transaction and requires RESOURCE_MODIFY permission. Note the endpoint name is misspelled "Datbase".',
  });

export const updateCostDatbaseInEstimateResponse = statusResponse.meta({
  description: "Response for update cost database in estimate",
});

export const saveEstimateItemsFromExcelSheetRequest = z
  .object({
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) to import the rows into.",
    }),
    items: z
      .array(
        z.object({
          HSN_SAC: z.string().optional().meta({
            description:
              "HSN/SAC tax code for a manual (uncoded) row; for coded rows it is overwritten from the cost-database lookup.",
          }),
          description: z.string().optional().meta({
            description:
              "Line description for a manual (uncoded) row; for coded rows it is overwritten from the cost-database item.",
          }),
          level: z.number().int().nonnegative().meta({
            description: "Row depth in the imported hierarchy (root = 0); drives parent/child nesting during import.",
          }),
          // Integer-only: parent is another row's rowNo (an integer). A fractional value can never match
          // a rowNo, so the row would silently fall through to root instead of nesting as intended.
          parent: z.number().int().nullable().meta({
            description:
              "The parent row's `rowNo` value (NOT the array index) — set this equal to the parent row's `rowNo` field; null for a root/top-level row. The tree is reconstructed by matching each row's `parent` against another row's `rowNo`. Note: 0 is NOT a valid parent — no row has rowNo 0 and 0 is treated as falsy, so a row with parent 0 becomes a root.",
          }),
          // NOT tightened: the service overwrites items[].purchaseUnit / parentId from a code-based
          // cost-DB lookup + hierarchy resolution (the input value is ignored), so an Excel row may
          // legitimately carry a unit NAME / arbitrary value here.
          purchaseUnit: z.string().optional().meta({
            description:
              "For coded rows this is OVERWRITTEN server-side from the cost-database item's purchase unit (input ignored); may carry a raw unit label from the sheet.",
          }),
          rowNo: z.number().nullable().meta({
            description:
              "Source spreadsheet row number; the stable id that other rows' `parent` points at to build the tree (a child sets its `parent` to this row's rowNo). 1-based / offset from the sheet, NOT the array index.",
          }),
          code: z.string().optional().meta({
            description:
              "Cost-database item code: if present the row is resolved against the estimate's linked cost DB (whole import fails if no DB is linked or the code is not found); absent/empty = manual item.",
          }),
          quantity: z
            .number()
            .nonnegative()
            .nullable()
            .optional()
            .meta({ description: "Item quantity for a leaf row (non-negative, matching the single-insert endpoint)." }),
          rate: z.number().nonnegative().nullable().optional().meta({
            description:
              "Unit rate for a manual row (non-negative, matching the single-insert endpoint); for coded rows it is overwritten from the cost-database item.",
          }),
          parentId: z.string().optional().meta({
            description: "OVERWRITTEN server-side from the resolved hierarchy; input is ignored.",
          }),
        }),
      )
      .meta({
        description:
          "Parsed spreadsheet rows to import as BOQ items, ordered so a parent appears before its children (parent references are by row index).",
      }),
  })
  .meta({
    description:
      "Bulk-import parsed Excel rows as BOQ items into the estimate's current version (RESOURCE_MODIFY). Rows carrying a `code` are resolved against the estimate's linked cost database (the whole import fails if the estimate has no linked cost DB or a code is not found); uncoded rows are saved as manual items, with parent/hierarchy resolved server-side. Pass estimateId (from getEstimateList) plus the `items` array (each with level and parent index).",
  });

export const saveEstimateItemsFromExcelSheetResponse = statusResponse.meta({
  description: "Response for save estimate items from excel sheet",
});

export const updatedEstimateItemSerialRequest = z
  .object({
    fromIndex: z
      .number()
      .int()
      .nonnegative()
      .meta({ description: "Current zero-based position of the moved item among its siblings." }),
    dropIndex: z.number().int().nonnegative().meta({
      description:
        "Target zero-based position among the siblings to move the item to; affected siblings' serials shift accordingly.",
    }),
    parentId: parentIdStringSchema.meta({
      description:
        'The moved item\'s parent itemId, or "0" for a top-level item — scopes which sibling group is reordered.',
    }),
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) containing the item.",
    }),
    itemId: objectIdStringSchema.meta({
      description: "The moved item's itemId (ObjectId 24-hex, from getEstimateItems).",
    }),
  })
  .meta({
    description:
      "Reorder a BOQ item within its parent group (drag-and-drop), moving it from `fromIndex` to `dropIndex` and shifting the affected siblings' serial values; enforces RESOURCE_MODIFY. Pass estimateId (from getEstimateList), the moved item's itemId (from getEstimateItems), and its parentId ('0' for top level, otherwise the parent item's id). Only changes ordering, not quantities or rates.",
  });

export const updatedEstimateItemSerialResponse = statusResponse.meta({
  description: "Response for update estimate item serial",
});

// Controller expects: value.params.estimateId, value.params.estimateParentId
// Service getValidItems receives: { estimateId, estimateParentId, skip, take, requiresCounts, ...value }
export const getValidItemsRequest = z
  .object({
    value: z.object({
      // estimateId reaches `new ObjectId(...)`; enforce hex (length(24) alone let non-hex through).
      estimateId: objectIdString.meta({
        description:
          "The estimate's _id (ObjectId 24-hex, from getEstimateList); the estimate must have a linked cost database or a DSR-not-linked error is returned.",
      }),
      // estimateParentId is either the "0" root sentinel or an item ObjectId. The service does
      // `if (estimateParentId !== "0") new ObjectId(estimateParentId)`, so an empty/non-hex string
      // (which z.string() allowed) throws. Restrict to "0" or a 24-char hex ObjectId.
      estimateParentId: z
        .string()
        .refine((v) => v === "0" || /^[a-fA-F0-9]{24}$/.test(v), "estimateParentId must be '0' or a valid ObjectId")
        .meta({
          description:
            'Position in the BOQ tree to find valid DSR link targets for: "0" for the root, otherwise the parent item\'s itemId (from getEstimateItems). The service walks up to the nearest DSR ancestor and returns its child DSR items.',
        }),
    }),
  })
  .meta({
    description:
      "List the cost-database (DSR) items that are valid link targets for a given position in an estimate's BOQ tree. Send value.estimateId (from getEstimateList) and value.estimateParentId — '0' for the root, otherwise the parent item's itemId. Enforces RESOURCE_VIEW and requires the estimate to have a linked cost database (returns a DSR-not-linked error otherwise). It walks the parent chain to find the nearest DSR ancestor and returns its child DSR items.",
  });

export const getValidItemsResponse = extendStatusResponse({
  result: z
    .array(z.looseObject({ itemId: z.union([z.string(), z.instanceof(Object)]).optional() }))
    .optional()
    .meta({ description: "DSR/cost-database items valid as link targets for the estimate item" }),
}).meta({
  description: "Response for get valid items",
});

// ============================================================================
// Get Estimate Total (Server is SSOT)
// ============================================================================

/**
 * Request schema for getting the total amount of an estimate.
 * Used to fetch the calculated total for BOQ items.
 */
export const getEstimateTotalRequest = z
  .object({
    estimateId: objectIdString.meta({
      description: "The unique identifier of the estimate",
      example: "64b2c123df890abc1234abcd",
    }),
    versionId: objectIdString.optional().meta({
      description: "Optional version ID to get total for a specific version",
      example: "64b2c123df890abc1234abcd",
    }),
  })
  .strict()
  .meta({
    description:
      "Return the numeric total amount of an estimate. Pass estimateId (from getEstimateList); optional versionId returns that specific version's amount but is scoped to the estimate — a versionId belonging to another estimate (or matching none) is rejected as not-found. Enforces RESOURCE_VIEW. This is estimate-scoped and returns only the total number, unlike getEstimateAdditionalCostTotal.",
  });

/**
 * Response schema for getting the total amount of an estimate.
 * Returns either a success with the total amount or an error.
 */
export const getEstimateTotalResponse = z
  .object({
    status: z.boolean().meta({
      description: "Indicates whether the operation was successful",
    }),
    data: z.number().nonnegative().optional().meta({
      description: "The total amount of the estimate (only present on success)",
      example: 150000.5,
    }),
    errorMessage: z.string().optional().meta({
      description: "Error message when status is false",
    }),
    errorCode: z.number().optional().meta({
      description: "Numeric error code for programmatic handling",
    }),
  })
  .strip()
  .meta({
    description: "Response for get estimate total",
  });

// ============================================================================
// Type exports for frontend consumption (Server is SSOT)
// ============================================================================

// Settings Types
export type SaveEstimateSettingsParams = z.infer<typeof saveEstimateSettingsSchema>;

// Estimate List Types
export type EstimateListParams = z.infer<typeof estimateListParamsSchema>;
export type EstimateListQuery = z.infer<typeof estimateListQuerySchema>;
export type EstimateListItem = z.infer<typeof estimateListItemSchema>;
export type EstimateListInsertValue = z.infer<typeof estimateListInsertValueSchema>;
export type GetEstimateListRequest = z.infer<typeof getEstimateListRequestSchema>;
export type GetEstimateListResponse = z.infer<typeof getEstimateListResponseSchema>;
// Estimate mutation endpoint request types (split from the getEstimateList action-dispatcher)
export type CreateEstimateRequest = z.infer<typeof createEstimateRequest>;
export type UpdateEstimateDataRequest = z.infer<typeof updateEstimateDataRequest>;
export type DeleteEstimateRequest = z.infer<typeof deleteEstimateRequest>;
/** Shape passed to getEstimateList service (no-action branch: spread of the query fields) */
/**
 * Service-side params for `getEstimateList`. Drops `portalId` because the controller
 * injects it from `AuthContext` (typed as `ObjectId`); leaving it on the type would
 * conflict with the wire-format `string` declaration in `estimateListQuerySchema`.
 */
export type GetEstimateListServiceParams = Omit<EstimateListQuery, "portalId">;
/** Shape passed to deleteEstimate service */
export type DeleteEstimateServiceParams = { _id: string };

// Estimate Data Types
export type GetEstimateDataRequest = z.infer<typeof getEstimateDataRequestSchema>;
export type GetEstimateDataResponse = z.infer<typeof getEstimateDataResponseSchema>;
export type EstimateCurrency = z.infer<typeof estimateCurrencySchema>;
export type EstimateData = z.infer<typeof estimateDataSchema>;

// Estimate Total Types
export type GetEstimateTotalRequest = z.infer<typeof getEstimateTotalRequest>;
export type GetEstimateTotalResponse = z.infer<typeof getEstimateTotalResponse>;

// Additional Cost Types
export type SaveAdditionalCostRequest = z.infer<typeof saveAdditionalCostRequest>;
export type SaveAdditionalCostResponse = z.infer<typeof saveAdditionalCostResponse>;
export type AdditionalCostReference = z.infer<typeof additionalCostReferenceSchema>;
export type AdditionalCostItem = z.infer<typeof additionalCostItemSchema>;

// Estimate Version Types
export type EstimateVersionEntry = z.infer<typeof estimateVersionEntrySchema>;
export type CreateEstimateVersionRequest = z.infer<typeof createEstimateVersionRequest>;
export type CreateEstimateVersionResponse = z.infer<typeof createEstimateVersionResponse>;
export type RestoreEstimateVersionRequest = z.infer<typeof restoreEstimateVersionRequest>;
export type RestoreEstimateVersionResponse = z.infer<typeof restoreEstimateVersionResponse>;
export type GetEstimateVersionRequest = z.infer<typeof getEstimateVersionRequest>;
export type GetEstimateVersionResponse = z.infer<typeof getEstimateVersionResponse>;

// DSR Version Check Types
export type CheckIfDsrVersionIsLatestRequest = z.infer<typeof checkIfDsrVersionIsLatestRequest>;
export type CheckIfDsrVersionIsLatestResponse = z.infer<typeof checkIfDsrVersionIsLatestResponse>;

// Change Item Type Types
export type ChangeItemTypeInEstimateRequest = z.infer<typeof changeItemTypeInEstimateRequest>;
export type ChangeItemTypeInEstimateResponse = z.infer<typeof changeItemTypeInEstimateResponse>;

// Update Cost Database in Estimate Types
export type UpdateCostDatabaseInEstimateRequest = z.infer<typeof updateCostDatbaseInEstimateRequest>;
export type UpdateCostDatabaseInEstimateResponse = z.infer<typeof updateCostDatbaseInEstimateResponse>;

// Add Files to Estimate Types
export type AddFilesToEstimateRequest = z.infer<typeof addFilesToEstimateRequest>;
export type AddFilesToEstimateResponse = z.infer<typeof addFilesToEstimateResponse>;

// Get Estimate Files Types
export type GetEstimateFilesRequest = z.infer<typeof getEstimateFilesRequest>;
export type GetEstimateFilesRequestValue = NonNullable<NonNullable<GetEstimateFilesRequest>["value"]>;
export type GetEstimateFilesResponse = z.infer<typeof getEstimateFilesResponse>;
export type RemoveFileFromEstimateRequest = z.infer<typeof removeFileFromEstimateRequest>;
/** Shape passed to removeFileFromEstimate service (key + spread value from the /removeFileFromEstimate route) */
export type RemoveFileFromEstimateServiceParams = GetEstimateFilesRequestValue & { fileVersionId: string };

// Get Estimate Items Types
export type GetEstimateItemsRequest = z.infer<typeof getEstimateItemsRequest>;
export type GetEstimateItemsRequestValue = NonNullable<GetEstimateItemsRequest["value"]>;
export type GetEstimateItemsResponse = z.infer<typeof getEstimateItemsResponse>;
export type SaveEstimateItemValueSchema = z.infer<typeof estimateItemValueSchema>;
// Item mutation endpoint request types (split from the getEstimateItems action-dispatcher)
export type InsertEstimateItemRequest = z.infer<typeof insertEstimateItemRequest>;
export type UpdateEstimateItemRequest = z.infer<typeof updateEstimateItemRequest>;
export type DeleteEstimateItemRequest = z.infer<typeof deleteEstimateItemRequest>;
export type EstimateItemMutationParams = z.infer<typeof estimateItemsTopParamsSchema>;
/**
 * Frontend-only input for the `saveItemInEstimate` dispatcher, which routes to the
 * /insertEstimateItem, /updateEstimateItem and /deleteEstimateItem endpoints by `action`.
 * (Not a wire schema — each endpoint validates its own dedicated request above.)
 */
export type SaveItemInEstimatePayload = {
  action: "insert" | "update" | "remove";
  value?: SaveEstimateItemValueSchema;
  key?: string | string[];
  params?: EstimateItemMutationParams;
};
/** Shape passed to saveEstimateItem service (action="insert" branch + extras from internal callers) */
export type SaveEstimateItemServiceParams = SaveEstimateItemValueSchema & {
  estimateId: string;
  estimateVersion?: Record<string, any>;
  forceCorrectItemPlacement?: boolean;
  hasChild?: boolean;
  estimateDetails?: Record<string, any>;
  session?: ClientSession;
};
/** Shape passed to updateEstimateItem service (action="update" branch + extras from internal callers) */
export type UpdateEstimateItemServiceParams = SaveEstimateItemValueSchema & {
  estimateId: string;
  accountDetail?: Record<string, any>;
  estimateVersion?: Record<string, any>;
  returnUpdatedData?: boolean;
  session?: ClientSession;
};
/** Shape passed to deleteEstimateItem service (action="remove" branch) */
export type DeleteEstimateItemServiceParams = {
  itemId: string | string[];
  estimateId: string;
  session?: ClientSession;
};

// Get Estimate Items Remote Types
export type GetEstimateItemsRemoteRequest = z.infer<typeof getEstimateItemsRemoteRequest>;
export type GetEstimateItemsRemoteResponse = z.infer<typeof getEstimateItemsRemoteResponse>;

// Get Estimate Item Types
export type GetEstimateItemRequest = z.infer<typeof getEstimateItemRequest>;
export type GetEstimateItemResponse = z.infer<typeof getEstimateItemResponse>;

// Get Child Estimate Items Types
export type GetChildEstimateItemsResponse = z.infer<typeof getChildEstimateItemsResponse>;

// Get Additional Cost Types
export type GetAdditionalCostRequest = z.infer<typeof getAdditionalCostRequest>;
export type DeleteAdditionalCostRequest = z.infer<typeof deleteAdditionalCostRequest>;
export type GetAdditionalCostResponse = z.infer<typeof getAdditionalCostResponse>;
/** Shape passed to getAdditionalCost service (no-action branch: value spread + sessionUserId) */
export type GetAdditionalCostServiceParams = {
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
  where?: unknown;
  sorted?: unknown;
  estimateId?: string;
  versionId?: string;
};
/** Shape passed to deleteAdditionalCost service (action="remove" branch) */
export type DeleteAdditionalCostServiceParams = {
  uniqueIds: string[];
  estimateId: string;
};

// Get Estimate Additional Cost Total Types
export type GetEstimateAdditionalCostTotalRequest = z.infer<typeof getEstimateAdditionalCostTotalRequest>;
export type GetEstimateAdditionalCostTotalResponse = z.infer<typeof getEstimateAdditionalCostTotalResponse>;

// Get CPWD Estimate List Types
export type GetCpwdEstimateListRequest = z.infer<typeof getCpwdEstimateListRequest>;
export type GetCpwdEstimateListResponse = z.infer<typeof getCpwdEstimateListResponse>;

// Change Display Unit Types
export type ChangeDisplayUnitRequest = z.infer<typeof changeDisplayUnitRequest>;
export type ChangeDisplayUnitResponse = z.infer<typeof changeDisplayUnitResponse>;

// Save Estimate Item Types
export type SaveEstimateItemRequest = z.infer<typeof saveEstimateItemRequest>;
export type SaveEstimateItemResponse = z.infer<typeof saveEstimateItemResponse>;

// Get Estimate Versions Latest Types
export type GetEstimateVersionsLatestRequest = z.infer<typeof getEstimateVersionsLatestRequest>;
export type GetEstimateVersionsLatestResponse = z.infer<typeof getEstimateVersionsLatestResponse>;

// Update Manual Measurement Sheet Types
export type UpdateManualMeasurementSheetResponse = z.infer<typeof updateManualMeasurementSheetResponse>;

// Save Estimate Items From Excel Sheet Types
export type SaveEstimateItemsFromExcelSheetRequest = z.infer<typeof saveEstimateItemsFromExcelSheetRequest>;
export type SaveEstimateItemsFromExcelSheetResponse = z.infer<typeof saveEstimateItemsFromExcelSheetResponse>;

// Updated Estimate Item Serial Types
export type UpdatedEstimateItemSerialRequest = z.infer<typeof updatedEstimateItemSerialRequest>;
export type UpdatedEstimateItemSerialResponse = z.infer<typeof updatedEstimateItemSerialResponse>;

// Get Valid Items Types
export type GetValidItemsRequest = z.infer<typeof getValidItemsRequest>;
export type GetValidItemsRequestValue = GetValidItemsRequest["value"];
export type GetValidItemsResponse = z.infer<typeof getValidItemsResponse>;

// Create Merge Estimate Types
export type CreateMergeEstimateRequest = z.infer<typeof createMergeEstimateRequest>;
export type CreateMergeEstimateResponse = z.infer<typeof createMergeEstimateResponse>;

// Charges List Types
export type ChargesListResponse = z.infer<typeof chargesListResponse>;

// Upgrade File Version Types
export type UpgradeFileVersionRequest = z.infer<typeof upgradeFileVersionRequest>;
export type UpgradeFileVersionResponse = z.infer<typeof upgradeFileVersionResponse>;

// Save Item Details Types
export type SaveItemDetailsRequest = z.infer<typeof saveItemDetailsRequest>;
export type SaveItemDetailsResponse = z.infer<typeof saveItemDetailsResponse>;

// Update Estimate Version Note Types
export type UpdateEstimateVersionNoteRequest = z.infer<typeof updateEstimateVersionNoteRequest>;
export type UpdateEstimateVersionNoteResponse = z.infer<typeof updateEstimateVersionNoteResponse>;

// Delete Estimate Version Note Types
export type DeleteEstimateVersionNoteRequest = z.infer<typeof deleteEstimateVersionNoteRequest>;
export type DeleteEstimateVersionNoteResponse = z.infer<typeof deleteEstimateVersionNoteResponse>;

export const checkIfDsrVersionIsLatestRequest = z
  .object({
    // ObjectId-format validation: estimateId reaches `new ObjectId(...)` via getEstimateData
    // and fetchLatestEstimateVersion; a non-hex string throws BSONError.
    estimateId: objectIdString.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose linked DSR version to check.",
    }),
  })
  .meta({
    description:
      "Read-only check of whether the estimate's linked cost database (DSR) is on the DSR's latest published version; returns status true when up-to-date, and also true when the estimate has no DSR linked. Pass estimateId (from getEstimateList). Requires RESOURCE_VIEW permission. Does not modify anything — use updateCostDatbaseInEstimate to actually re-link/upgrade the DSR.",
  });

export const checkIfDsrVersionIsLatestResponse = statusResponse.meta({
  description: "Response for check if DSR version is latest",
});

export const getEstimateDataRequest = getEstimateDataRequestSchema.meta({
  description:
    "Fetch a single estimate's header/settings by _id (the estimate id, from getEstimateList): returns the merged estimate + latest-version data — name, formatted counter, display-unit config, display settings, costDatabase currency, estimateType, constituent-version details and the caller's userPermissions. Optional versionId targets a specific version. Enforces RESOURCE_VIEW. This returns estimate-level data, not BOQ line items (use getEstimateItems) or the numeric total (use getEstimateTotal).",
});

export const getEstimateDataResponse = getEstimateDataResponseSchema.meta({
  description: "Response for get estimate data",
});

export const createMergeEstimateRequest = z
  .object({
    mergeEstimateName: zPlainTextRequired.meta({
      description: "Display name for the new merge estimate (plain text, required).",
    }),
    spaceId: objectIdString.meta({
      description:
        "Space ObjectId (24-hex, from projects_getSpacesDataByProjectId) to create the merge estimate in; create permission on this space is required.",
    }),
    projectId: objectIdString.meta({
      description: "Project ObjectId (24-hex, from getIncludedProjectsController) the merge estimate belongs to.",
    }),
    // Elements are estimate ObjectIds fed to new ObjectId(id); reject duplicates (which would
    // double-count amounts) and non-hex strings at the boundary.
    constituentEstimateIds: z
      .array(objectIdString)
      .refine((ids) => new Set(ids).size === ids.length, "Duplicate constituent estimate ids are not allowed")
      .meta({
        description:
          "Estimate _ids (each ObjectId 24-hex, from getEstimateList) to merge; must be non-empty and duplicate-free (duplicates would double-count amounts). Send the ids, not the names.",
      }),
  })
  .meta({
    description:
      "Create a NEW merge-type estimate that aggregates the BOQ items of several existing estimates. Requires mergeEstimateName, spaceId (from projects_getSpacesDataByProjectId), projectId (from getIncludedProjectsController) and a non-empty, duplicate-free constituentEstimateIds array (each id from getEstimateList). Delegates to the same create path as createEstimate (estimateType='merge'), so it enforces create permission on the space. Use createEstimate for a single standard estimate.",
  });

export const createEstimateResponse = extendStatusResponse({
  data: z.object({ _id: z.string() }).optional(),
}).meta({
  description: "Response for /createEstimate — on success returns the newly created estimate's _id in `data`.",
});

export const createMergeEstimateResponse = extendStatusResponse({
  data: z.object({ _id: z.string() }).optional(),
}).meta({
  description: "Response for create merge estimate — on success returns the merged estimate's _id in `data`.",
});

// Per-endpoint value schema for the charges listing (do not mutate shared gridValueSchema).
// `where` uses the filterable columns; `sorted` uses the DIVERGENT sortable columns
// (modifiedBy.name vs the filterable modifiedBy._id) — see CHARGES_LIST_SORTABLE_FIELDS.
const chargesListValueSchema = gridValueSchema.extend({
  where: chargesListWhere,
  sorted: sortConfigFor(CHARGES_LIST_SORTABLE_FIELDS, { patterns: dynamicCustomFieldPatterns }).meta({
    description:
      "Grid sort restricted to the charges columns (name/percentage/modifiedBy.name/modifiedDate) plus per-project custom-field columns.",
  }),
});

// List-only request for /chargesList. Charge create/update/delete now have dedicated endpoints
// (createChargeRequest / updateChargeRequest / deleteChargeRequest).
export const chargesListRequest = z
  .object({
    value: chargesListValueSchema.optional().meta({
      description:
        "Grid query (skip/take/sorted/where/requiresCounts) over portal charges; where is restricted to the charges columns (name/percentage/modifiedBy._id/modifiedDate). Omit for the default page.",
    }),
  })
  .meta({
    description:
      "List the portal-level charges (name + percentage) stored on the current portal's portals.charges[] array, paginated/filtered via the value grid query. The portal is taken from the auth context (not a caller input). Enforces VIEW_LIST_OF_CHARGES. These are reusable portal charges applied in rate analysis — NOT an estimate's additional costs (see getAdditionalCost).",
  });

// Shared charge field shape (name + percentage).
const chargeFieldsShape = {
  name: zPlainTextRequired.meta({
    description:
      "Charge name (plain text, required); must be unique among the portal's charges (duplicate names are rejected).",
  }),
  percentage: z.number().nonnegative().meta({
    description:
      "Charge percentage applied in rate analysis (e.g. 18 for 18%). Non-negative: a negative value would silently subtract from every BOQ rate that uses this charge.",
  }),
};

export const createChargeRequest = z.object({ ...chargeFieldsShape }).meta({
  description:
    "Create a new portal-level charge (name + percentage) pushed onto the current portal's portals.charges[]; duplicate names are rejected. The portal is taken from the auth context, so only name and percentage are needed. Enforces CREATE_CHARGES.",
});

export const updateChargeRequest = z
  .object({
    _id: objectIdStringSchema.meta({
      description:
        "The charge's _id (ObjectId 24-hex, from chargesList) to update. If the charge is currently used in rate analysis only a RENAME is blocked; a percentage-only change (name unchanged) still succeeds.",
    }),
    ...chargeFieldsShape,
  })
  .meta({
    description:
      "Update an existing portal charge's name and percentage, identified by its _id (the charge _id from chargesList). The update is blocked if the charge is already in use (rate analysis) and a rename to a name that collides with another charge is rejected. Enforces EDIT_CHARGES; the portal comes from the auth context.",
  });

export const deleteChargeRequest = z
  .object({
    keys: z.array(objectIdStringSchema).min(1).meta({
      description:
        "Non-empty array of charge _ids (each ObjectId 24-hex, from chargesList) to delete; if any is currently in use in rate analysis the whole delete is blocked.",
    }),
  })
  .meta({
    description:
      "Delete one or more portal charges by _id. Pass keys as an array of charge _ids (from chargesList); at least one is required. Any charge currently in use (rate analysis) blocks the whole delete. Enforces DELETE_CHARGES; the portal comes from the auth context.",
  });

export type ChargesListRequest = z.infer<typeof chargesListRequest>;
export type CreateChargeRequest = z.infer<typeof createChargeRequest>;
export type UpdateChargeRequest = z.infer<typeof updateChargeRequest>;
export type DeleteChargeRequest = z.infer<typeof deleteChargeRequest>;

export const chargesListResponse = extendStatusResponse({
  result: z.array(z.record(z.string(), z.unknown())).optional(),
  count: z.number().nonnegative().optional(),
  errorMessage: z.string().optional(),
}).meta({
  description: "Response for charges list",
});

export const upgradeFileVersionRequest = z
  .object({
    // Hex-validate (length(24) alone let non-hex strings through to new ObjectId -> BSONError).
    estimateId: objectIdString.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) whose attached file to upgrade.",
    }),
    fileVersionId: objectIdString.meta({
      description:
        "The CURRENTLY attached file version's id (ObjectId 24-hex, from getEstimateFiles rows where isFileUpdateRequired is true) — the service upgrades this to the file's newer version.",
    }),
    reRunWizard: z.boolean().optional().meta({
      description:
        "If true, re-run the takeoff wizard against the upgraded file version (re-extracts takeoff quantities).",
    }),
  })
  .meta({
    description:
      "Upgrade the file currently attached to an estimate to its newer version, updating linked takeoffs/measurements and optionally re-running the takeoff wizard (reRunWizard). Pass estimateId (from getEstimateList) and fileVersionId — the CURRENTLY attached version's id from getEstimateFiles (rows where isFileUpdateRequired is true). Enforces RESOURCE_MODIFY and runs inside a transaction.",
  });

export const upgradeFileVersionResponse = statusResponse.meta({
  description: "Response for upgrade file version",
});

export const saveItemDetailsRequest = z
  .object({
    itemId: objectIdStringSchema.meta({
      description: "The item's itemId (ObjectId 24-hex, from getEstimateItems) to save details on.",
    }),
    code: z.string().optional().meta({ description: "Item code/reference to set." }),
    description: zSafeHtml.optional().meta({ description: "Item description (sanitized HTML) to set." }),
    // NOT tightened: on the DSR branch (source=dsr_level_unit) this is forwarded to saveDsrItemData
    // where purchaseUnit is a UNIT NAME resolved via lookup, not an ObjectId. Keep it permissive.
    purchaseUnit: z.string().optional().meta({
      description:
        "Purchase unit. Format depends on `source`: for 'estimate_level_unit' it is a PurchaseUnits ObjectId; for 'dsr_level_unit' it is a unit NAME resolved via lookup. Not tightened to hex for this reason.",
    }),
    rate: z.number().optional().meta({ description: "Unit rate to set on the item." }),
    // sourceId is forwarded as dsrId/estimateId (both ObjectIds).
    sourceId: objectIdStringSchema.meta({
      description:
        "ObjectId (24-hex) of the target: when source='estimate_level_unit' it is the estimateId (from getEstimateList); when source='dsr_level_unit' it is the cost-database/dsrId. Select via `source`.",
    }),
    source: z.string().meta({
      description:
        "Target/permission selector: 'estimate_level_unit' edits the estimate's BOQ item (RESOURCE_MODIFY, sourceId=estimateId); 'dsr_level_unit' edits the underlying cost-database item (EDIT_COSTDATABASE, sourceId=dsrId).",
    }),
    parentId: parentIdStringSchema
      .optional()
      .meta({ description: 'Parent item\'s itemId, or "0" for a top-level item; used for item placement/context.' }),
    level: z.number().optional().meta({ description: "The item's depth in the BOQ tree (root = 0)." }),
    // rateAnalysis accepts both array format and object format for backwards compatibility
    rateAnalysis: z.unknown().optional().meta({
      description:
        "Rate-analysis breakdown (labour/material/sub-components). Stored as-is (shape not validated); accepts both the legacy array and the object form.",
    }),
    isRateAnalysisEnable: z.boolean().optional().meta({
      description: "If true, the item's rate is driven by its rateAnalysis breakdown rather than a flat rate.",
    }),
    quantity: z
      .number()
      .optional()
      .meta({ description: "Item quantity to set; the amount is recomputed after saving." }),
  })
  .meta({
    description:
      "Save details (code, description, purchaseUnit, rate, rateAnalysis, quantity) on one item inside a transaction; the `source` field selects the target and permission: 'estimate_level_unit' updates the estimate's BOQ item (sourceId = estimateId, RESOURCE_MODIFY via updateEstimateItem) while 'dsr_level_unit' edits the underlying cost-database item (sourceId = dsrId, requires EDIT_COSTDATABASE permission). Get itemId from getEstimateItems and sourceId from getEstimateList (estimate) or the cost-database/DSR list.",
  });

export const saveItemDetailsResponse = z
  .object({
    status: z.boolean().meta({ description: "Whether the operation was successful" }),
    result: z.record(z.string(), z.unknown()).optional().meta({ description: "The saved item data" }),
    errorMessage: z.string().optional().meta({ description: "Error message when status is false" }),
    errorCode: z.number().optional().meta({ description: "Error code when status is false" }),
  })
  .strip()
  .meta({
    description: "Response for save item details",
  });

export const updateEstimateVersionNoteRequest = z
  .object({
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) that owns the version.",
    }),
    estimateVersionId: objectIdStringSchema.meta({
      description:
        "The published version's EstimateVersions _id (ObjectId 24-hex, from getEstimateVersion) to annotate.",
    }),
    versionNumber: z.number().meta({
      description:
        "The published version's version number; the write is scoped by estimateId + estimateVersionId + versionNumber together, so a mismatch is rejected as not found.",
    }),
    note: zSafeHtml
      .pipe(z.string().max(20000, "Note is too long."))
      .meta({ description: "Note content (sanitized HTML, max 20000 chars) to set/overwrite on the version." }),
  })
  .meta({
    description:
      "Sets/overwrites the free-text note on one specific published estimate version. The version write is scoped by estimateId + estimateVersionId + versionNumber together, so a versionNumber or version id that does not belong to the given estimate is rejected as not found. Pass ids from getEstimateVersion (estimateId originally from getEstimateList). Requires RESOURCE_MODIFY permission.",
  });

export const updateEstimateVersionNoteResponse = statusResponse.meta({
  description: "Response for update estimate version note",
});

export const deleteEstimateVersionNoteRequest = z
  .object({
    estimateId: objectIdStringSchema.meta({
      description: "The estimate's _id (ObjectId 24-hex, from getEstimateList) that owns the version.",
    }),
    estimateVersionId: objectIdStringSchema.meta({
      description:
        "The published version's EstimateVersions _id (ObjectId 24-hex, from getEstimateVersion) whose note to remove.",
    }),
    versionNumber: z.number().meta({
      description:
        "The published version's version number; scoped with estimateId + estimateVersionId, so a version not belonging to the estimate (or with no note) is rejected.",
    }),
  })
  .meta({
    description:
      "Removes the note from one specific published estimate version (unsets the note field). Scoped by estimateId + estimateVersionId + versionNumber, so a version not belonging to the given estimate — or one with no existing note — is rejected. Pass ids from getEstimateVersion (estimateId from getEstimateList). Requires RESOURCE_MODIFY permission. Counterpart of updateEstimateVersionNote.",
  });

export const deleteEstimateVersionNoteResponse = statusResponse.meta({
  description: "Response for delete estimate version note",
});

export const saveEstimateSettingsRequest = saveEstimateSettingsSchema.meta({
  description:
    "Saves display/formatting preferences for an estimate — decimal-place rounding for quantity/measurement/takeoff/rate/amount, digitGrouping, largeNumber abbreviation, and numberingSystem — writing them onto the estimate's latest version (and its Estimates.latestEstimateVersion embed) as a new SETTINGS micro-version. The request body IS the settings object directly (estimateId plus the preference fields, not wrapped in value). estimateId from getEstimateList. Requires RESOURCE_MODIFY permission.",
});
